import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { useSentinelChat } from './SentinelChatProvider'

/** Drawer content: History (past conversations) and Saved Questions (bookmarked prompts). */
const TAB_KEYS = ['history', 'saved']

export default function SentinelLibrary({ onSelect }) {
  const { t } = useTranslation()
  const {
    conversationId,
    loadConversation,
    saved,
    fetchSaved,
    deleteSavedQuestion,
    ask,
    conversations,
    fetchConversations,
  } = useSentinelChat()

  const [tab, setTab] = useState('history')
  const recent = conversations.items

  // Bucket past conversations by recency for scannable section headers.
  const historyGroups = (() => {
    const now = moment()
    const buckets = { today: [], thisWeek: [], earlier: [] }
    recent.forEach((c) => {
      const d = c.last_at ? moment(c.last_at) : null
      if (d && d.isSame(now, 'day')) buckets.today.push(c)
      else if (d && d.isAfter(now.clone().subtract(7, 'days'))) buckets.thisWeek.push(c)
      else buckets.earlier.push(c)
    })
    return ['today', 'thisWeek', 'earlier']
      .map((key) => ({ key, items: buckets[key] }))
      .filter((g) => g.items.length)
  })()

  useEffect(() => {
    fetchSaved()
    fetchConversations()
  }, [fetchSaved, fetchConversations])

  const openConversation = (id) => {
    loadConversation(id)
    onSelect?.()
  }
  // Saved questions are reusable prompts. Run them as a new turn in the active
  // conversation so the existing transcript remains visible.
  const runSaved = (q) => {
    ask(q.question)
    onSelect?.()
  }

  return (
    <div className="fd-library">
      <div className="fd-tabs" role="tablist" aria-label={t('Sentinel.V2.panelTitle')}>
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            id={`sentinel-tab-${key}`}
            className="fd-tab"
            role="tab"
            aria-selected={tab === key}
            aria-controls={`sentinel-panel-${key}`}
            onClick={() => setTab(key)}
          >
            {t(`Sentinel.V2.tabs.${key}`)}
            {key === 'saved' && saved.items.length ? ` · ${saved.items.length}` : ''}
          </button>
        ))}
      </div>

      <div className="fd-library__scroll">
        {tab === 'history' && (
          <div role="tabpanel" id="sentinel-panel-history" aria-labelledby="sentinel-tab-history">
            {conversations.loading && recent.length === 0 && (
              <div className="fd-body-sm px-2 py-3" style={{ color: 'var(--text-tertiary)' }}>
                {t('Sentinel.V2.loading')}
              </div>
            )}
            {!conversations.loading && recent.length === 0 && (
              <div className="fd-empty">
                <span className="fd-empty__icon">
                  <i className="fa-regular fa-comments" aria-hidden="true" />
                </span>
                <p className="fd-body-sm">{t('Sentinel.V2.history.empty')}</p>
              </div>
            )}
            {historyGroups.map((g) => (
              <div key={g.key} className="fd-conv-group">
                <div className="fd-eyebrow fd-conv-group__label">
                  {t(`Sentinel.V2.history.groups.${g.key}`)}
                </div>
                <div className="fd-conv-list">
                  {g.items.map((c) => (
                    <button
                      key={c.conversation_id}
                      className={`fd-conv${c.conversation_id === conversationId ? ' is-active' : ''}`}
                      aria-current={c.conversation_id === conversationId ? 'true' : undefined}
                      onClick={() => openConversation(c.conversation_id)}
                    >
                      <span className="fd-conv__icon">
                        <i className="fa-regular fa-comment" aria-hidden="true" />
                      </span>
                      <span className="fd-conv__body">
                        <span className="fd-conv__title">
                          {c.first_question || t('Sentinel.V2.history.untitled')}
                        </span>
                        {c.last_at && (
                          <span className="fd-conv__meta">{moment(c.last_at).fromNow()}</span>
                        )}
                      </span>
                      <i className="fa-solid fa-chevron-right fd-conv__chev" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'saved' && (
          <div role="tabpanel" id="sentinel-panel-saved" aria-labelledby="sentinel-tab-saved">
            {saved.loading && (
              <div className="fd-body-sm px-2 py-3" style={{ color: 'var(--text-tertiary)' }}>
                {t('Sentinel.V2.loading')}
              </div>
            )}
            {!saved.loading && saved.items.length === 0 && (
              <div className="fd-empty">
                <span className="fd-empty__icon">
                  <i className="fa-regular fa-star" aria-hidden="true" />
                </span>
                <p className="fd-body-sm">{t('Sentinel.V2.saved.empty')}</p>
              </div>
            )}
            <div className="fd-conv-list">
              {saved.items.map((q) => (
                <div key={q.id} className="fd-conv">
                  <button className="fd-conv__main" onClick={() => runSaved(q)}>
                    <span className="fd-conv__icon fd-conv__icon--saved">
                      <i className="fa-solid fa-star" aria-hidden="true" />
                    </span>
                    <span className="fd-conv__body">
                      <span className="fd-conv__title">{q.name}</span>
                      <span className="fd-conv__meta">{t('Sentinel.V2.saved.runAgain')}</span>
                    </span>
                  </button>
                  <button
                    className="fd-btn fd-btn--ghost fd-btn--icon fd-btn--sm fd-conv__trash"
                    aria-label={t('Sentinel.V2.saved.delete')}
                    onClick={() => deleteSavedQuestion(q.id)}
                  >
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

SentinelLibrary.propTypes = { onSelect: PropTypes.func }
